import { useHybridData } from "./useHybridData";
import { supabase } from "@/integrations/supabase/client";
import faqIndex from "@/data/generated/faqs-index.json";
import { useMemo } from "react";

export interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string | null;
  subcategory: string | null;
  treatment_id: string | null;
  is_featured: boolean | null;
  sort_order: number | null;
  created_at: string;
}

const fetchNewFAQs = async (lastBuildDate: string) => {
  const { data, error } = await supabase
    .from("faqs")
    .select("*")
    .gt("created_at", lastBuildDate)
    .order("sort_order", { ascending: true });

  if (error) {
    console.error("Error fetching new FAQs:", error);
    return null;
  }
  return data as FAQ[];
};

export function useAllFAQs() {
  return useHybridData<FAQ>({
    resourceName: "FAQs",
    staticData: faqIndex as unknown as FAQ[],
    fetchDynamic: fetchNewFAQs,
    queryKey: ["faqs"],
    sortFn: (a, b) => (a.sort_order || 999) - (b.sort_order || 999),
  });
}

export function useFAQs(treatmentSlug?: string, category?: string) {
  const { data: allFaqs } = useAllFAQs();

  const filteredFaqs = useMemo(() => {
    if (!allFaqs) return [];

    let treatmentFaqs: FAQ[] = [];
    let categoryFaqs: FAQ[] = [];
    let generalFaqs: FAQ[] = [];

    if (treatmentSlug) {
      treatmentFaqs = allFaqs.filter(f => f.subcategory === treatmentSlug);
    }
    
    if (category) {
      categoryFaqs = allFaqs.filter(f => f.category === category && !f.subcategory);
    }
    
    generalFaqs = allFaqs.filter(f => f.category === "General");

    // Client-side limiting logic from original hook
    // Limits: 2 treatment + 2 category + 3 general = 7 max roughly
    return [
      ...treatmentFaqs.slice(0, 2),
      ...categoryFaqs.slice(0, 2),
      ...generalFaqs.slice(0, 3)
    ];
  }, [allFaqs, treatmentSlug, category]);

  return {
    data: filteredFaqs,
    isLoading: false // Always instantaneous
  };
}
